import { effect, Injectable } from "@angular/core";
import { MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { DialogInvokingComponents } from "@models/generic-dialog.model";
import { TranslateService } from "@ngx-translate/core";
import { GenericDialogComponent } from "src/app/shared/generic-dialog/generic-dialog.component";
import { CustomTourService } from "../custom-tour.service";
import { firstValueFrom } from "rxjs";
import { Router } from "@angular/router";

@Injectable({
    providedIn: "root",
})
export class DialogService {
    private dialogRegistry: Map<MatDialogRef<GenericDialogComponent>, boolean> = new Map();
    private hasCompletedTour: boolean = false;

    constructor(
        private dialog: MatDialog,
        private translate: TranslateService,
        private customTourService: CustomTourService,
        private router: Router,
    ) {
        effect(() => {
            this.hasCompletedTour = this.customTourService.hasCompletedTour();
        });
    }

    public openDialog<T>(
        dialogData: T,
        width: string = "70%",
        options: Partial<MatDialogConfig> = {},
        maxWidth: string = "800px",
    ): MatDialogRef<GenericDialogComponent> {
        const dialogRef = this.dialog.open(GenericDialogComponent, {
            data: dialogData,
            width: width,
            maxWidth: maxWidth,
            autoFocus: false,
            restoreFocus: false,
            ...options,
        });

        // Add dialog to the stack with default close-all flag set to true
        this.dialogRegistry.set(dialogRef, true);

        dialogRef.afterClosed().subscribe((result) => {
            this.handleDialogResult(dialogRef, result);
        });

        return dialogRef;
    }

    private handleDialogResult<T>(dialogRef: MatDialogRef<GenericDialogComponent>, result: T): void {
        // Check if we should close other dialogs based on the dialog's flag
        if (this.dialogRegistry.get(dialogRef) !== false) {
            this.closeAllDialogs(result);
        }
    }

    public closeDialog<T>(dialogRef: MatDialogRef<GenericDialogComponent>, data?: T): void {
        dialogRef.close(data);
        this.dialogRegistry.delete(dialogRef);
    }

    private closeAllDialogs<T>(data?: T): void {
        this.dialogRegistry.forEach((_, dialogRef) => this.closeDialog(dialogRef, data));
    }

    // Optional method to set whether closing all dialogs is required for a particular dialog
    public setCloseAllFlag(dialogRef: MatDialogRef<GenericDialogComponent>, flag: boolean): void {
        if (this.dialogRegistry.has(dialogRef)) {
            this.dialogRegistry.set(dialogRef, flag);
        }
    }

    public async onBoardingDialog(): Promise<void> {
        const onBoardingData = {
            componentName: DialogInvokingComponents.OnBoarding,
            title: await this.getTranslation(
                this.hasCompletedTour ? "genericDialogBox.onBoarding.title2" : "genericDialogBox.onBoarding.title",
            ),
            content: await this.getTranslation(
                this.hasCompletedTour ? "genericDialogBox.onBoarding.content2" : "genericDialogBox.onBoarding.content",
            ),
            firstBtn: this.hasCompletedTour ? "" : await this.getTranslation("genericDialogBox.button.getStartedBtn"),
            secondBtn: await this.getTranslation(
                this.hasCompletedTour ? "genericDialogBox.button.closeBtn" : "genericDialogBox.button.skipBtn",
            ),
        };

        this.openDialog(onBoardingData, "40%", { disableClose: true })
            .afterClosed()
            .subscribe((response) => {
                if (response) {
                    this.customTourService.startDesignerTour();
                }
            });
    }

    private getTranslation(key: string, params?: object): Promise<string> {
        return firstValueFrom(this.translate.get(key, params));
    }

    public loginDialog(isDesigner: boolean = false, targetUrl?: string): void {
        this.router.navigate(["/login"]);
    }
}
